import { DynamoDBClient, QueryCommand, QueryCommandOutput } from '@aws-sdk/client-dynamodb';
import logger from '@ecg-marktplaats/aurora-node-logging';
import { property } from '@/server/utils/properties';
import staticFallback from '@/server/repositories/canonicalOverrideData';

const TENANT_DOMAINS: Record<string, string> = {
  mpnlnl: 'https://www.marktplaats.nl',
  twhnlbe: 'https://www.2dehands.be',
  twhfrbe: 'https://www.2ememain.be',
};

function getConfig() {
  return {
    enabled: property('canonical.overrides.enabled') === 'true',
    tableName: property('canonical.overrides.dynamodb.table') || 'canonical-urls',
    region: property('canonical.overrides.dynamodb.region') || 'eu-central-1',
    refreshIntervalMs: (Number(property('canonical.overrides.refresh.seconds')) || 300) * 1000,
    accessKeyId: property('canonical.overrides.dynamodb.access.key'),
    secretAccessKey: property('canonical.overrides.dynamodb.secret.key'),
  };
}

// Stored on globalThis so the Express (dist/) and Next.js (.next/server/) module instances
// share the same maps — they are separate webpack bundles in the same Node.js process.
type GlobalWithCanonicalMaps = typeof globalThis & {
  __canonicalOverrideMaps?: Map<string, Map<string, string>>;
  __canonicalRefreshTimers?: Map<string, ReturnType<typeof setInterval>>;
};
const g = globalThis as GlobalWithCanonicalMaps;
const overrideMaps: Map<string, Map<string, string>> = (g.__canonicalOverrideMaps ??= new Map());
const refreshTimers: Map<string, ReturnType<typeof setInterval>> = (g.__canonicalRefreshTimers ??= new Map());

function mergeWithFallback(dynamoMap: Map<string, string>): Map<string, string> {
  return new Map([...Object.entries(staticFallback), ...dynamoMap]);
}

async function loadFromDynamoDB(
  client: DynamoDBClient,
  tableName: string,
  tenant: string,
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  let lastEvaluatedKey: QueryCommandOutput['LastEvaluatedKey'] | undefined;

  do {
    const response: QueryCommandOutput = await client.send(
      new QueryCommand({
        TableName: tableName,
        IndexName: 'tenant-index',
        KeyConditionExpression: 'tenant = :t',
        ExpressionAttributeValues: { ':t': { S: tenant } },
        ProjectionExpression: '#p, canonicalTarget',
        ExpressionAttributeNames: { '#p': 'path' },
        ...(lastEvaluatedKey ? { ExclusiveStartKey: lastEvaluatedKey } : {}),
      }),
    );

    for (const item of response.Items ?? []) {
      const rawPath = item.path?.S;
      const target = item.canonicalTarget?.S;
      if (rawPath && target) {
        // Strip the "tenant#" composite prefix written by the ingest script.
        const path = rawPath.replace(/^[^#]+#/, '');
        map.set(path, target);
      }
    }

    lastEvaluatedKey = response.LastEvaluatedKey;
  } while (lastEvaluatedKey);

  return map;
}

export function initCanonicalOverrides(tenant: string): void {
  const { enabled, tableName, region, refreshIntervalMs, accessKeyId, secretAccessKey } = getConfig();

  if (!enabled) {
    overrideMaps.set(tenant, new Map());
    logger.info(
      `canonicalOverrides[${tenant}]: disabled via canonical.overrides.enabled — no overrides will be served`,
    );
    return;
  }

  // Seed static fallback immediately so early requests and DynamoDB failures always have data.
  overrideMaps.set(tenant, new Map(Object.entries(staticFallback)));

  const client = new DynamoDBClient({
    region,
    ...(accessKeyId && secretAccessKey ? { credentials: { accessKeyId, secretAccessKey } } : {}),
  });

  const refresh = async (label = 'loaded') => {
    const start = Date.now();
    try {
      const map = await loadFromDynamoDB(client, tableName, tenant);
      const finalMap = mergeWithFallback(map);
      overrideMaps.set(tenant, finalMap);
      logger.info(
        `canonicalOverrides[${tenant}]: ${label} ${map.size} entries from DynamoDB (${tableName}) in ${Date.now() - start}ms`,
      );

      // TEMP-DEBUG MP-22804: breakdown of loaded entries
      logger.info(
        `TEMP-DEBUG MP-22804 [${tenant}]: rawDynamoCount=${map.size}, finalMapSize=${finalMap.size}, staticFallbackCount=${Object.keys(staticFallback).length}`,
      );

      // TEMP-DEBUG MP-22804: probe specific keys
      const probeKeys = ['/q/kunstgras/', '/q/kruiwagen/', '/q/bloempotten/', '/q/audi+etron/'];
      const probeResults = probeKeys.map((k) => ({ key: k, present: finalMap.has(k) }));
      logger.info(`TEMP-DEBUG MP-22804 [${tenant}]: probeKeys=${JSON.stringify(probeResults)}`);

      // TEMP-DEBUG MP-22804: category/brand breakdown
      const breakdown: Record<string, number> = {};
      for (const [path] of map) {
        const match = path.match(/^\/[ql]\/([\w-]+)/);
        const segment = match ? match[1] : 'other';
        breakdown[segment] = (breakdown[segment] || 0) + 1;
      }
      logger.info(`TEMP-DEBUG MP-22804 [${tenant}]: pathSegmentBreakdown=${JSON.stringify(breakdown)}`);
    } catch (e) {
      logger.warn(
        `canonicalOverrides[${tenant}]: DynamoDB load failed after ${Date.now() - start}ms — using current cache (${overrideMaps.get(tenant)?.size ?? 0} entries)`,
        e,
      );
    }
  };

  void refresh();

  if (process.env.NODE_ENV !== 'test' && !refreshTimers.has(tenant)) {
    const timer = setInterval(async () => {
      await refresh('refreshed');
    }, refreshIntervalMs);
    timer.unref?.();
    refreshTimers.set(tenant, timer);
  }
}

export function stopCanonicalOverridesRefresh(tenant?: string): void {
  if (tenant) {
    const timer = refreshTimers.get(tenant);
    if (timer) {
      clearInterval(timer);
      refreshTimers.delete(tenant);
    }
  } else {
    for (const [t, timer] of refreshTimers) {
      clearInterval(timer);
      refreshTimers.delete(t);
    }
  }
}

export function getCanonicalOverride(tenant: string, path: string): string | undefined {
  const map = overrideMaps.get(tenant);
  // map is undefined only before initCanonicalOverrides has run for this tenant — serve static data.
  // map is an empty Map when the toggle is disabled — return undefined.
  const rawUrl = map ? map.get(path) : staticFallback[path];
  if (!rawUrl) return undefined;

  // Stored values may be full URLs (legacy static fallback) or path-only (DynamoDB).
  // Resolve path-only values using the tenant domain map.
  const override = rawUrl.startsWith('https://')
    ? rawUrl
    : TENANT_DOMAINS[tenant]
      ? `${TENANT_DOMAINS[tenant]}${rawUrl}`
      : undefined;

  if (!override) {
    logger.warn(`canonicalOverrides[${tenant}]: missing tenant domain for path-only override "${rawUrl}" — skipping`);
    return undefined;
  }

  logger.warn(`canonicalOverrides[${tenant}]: override hit for path ${path}`);
  return override;
}
